import { getCookie } from "@/lib/cookies";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { API_URL } from "@/env";

// const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000/api";

interface Integration {
  connected: boolean;
  email?: string;
  baseUrl?: string;
  organizationName?: string;
  projectSlug?: string;
  projects?: { slug: string; name: string; platform: string; id: string }[];
}

interface IntegrationsState {
  integrations: {
    jira: Integration;
    slack: Integration;
    linear: Integration;
    sentry: Integration;
  };
  selectedIntegration: "jira" | "slack" | "linear" | "sentry" | null;
  setSelectedIntegration: (
    integration: "jira" | "slack" | "linear" | "sentry" | null
  ) => void;
  checkJiraStatus: (workspaceId: string) => Promise<void>;
  disconnectJira: (workspaceId: string) => Promise<void>;
  checkLinearStatus: (workspaceId: string) => Promise<void>;
  disconnectLinear: (workspaceId: string) => Promise<void>;
  checkSentryStatus: (workspaceId: string) => Promise<void>;
  disconnectSentry: (workspaceId: string) => Promise<void>;
  fetchSentryProjects: (workspaceId: string) => Promise<void>;
  setSentryProject: (workspaceId: string, projectSlug: string) => Promise<void>;
}

export const useIntegrationsStore = create<IntegrationsState>()(
  persist(
    (set) => ({
      integrations: {
        jira: {
          connected: false,
        },
        slack: {
          connected: false,
        },
        linear: {
          connected: false,
        },
        sentry: {
          connected: false,
        },
      },
      selectedIntegration: null,

      setSelectedIntegration: (integration) => {
        set({ selectedIntegration: integration });
      },

      checkJiraStatus: async (workspaceId: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/jira/${workspaceId}/status`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          set((state) => ({
            integrations: {
              ...state.integrations,
              jira: {
                connected: data.connected || false,
                email: data.email,
                baseUrl: data.baseUrl,
              },
            },
          }));

          return data;
        } catch (error) {
          console.error("Failed to check Jira status:", error);
          set((state) => ({
            integrations: {
              ...state.integrations,
              jira: {
                connected: false,
              },
            },
          }));
        }
      },

      disconnectJira: async (workspaceId: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/jira/${workspaceId}/disconnect`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            set((state) => ({
              integrations: {
                ...state.integrations,
                jira: {
                  connected: false,
                },
              },
              selectedIntegration:
                state.selectedIntegration === "jira"
                  ? null
                  : state.selectedIntegration,
            }));
          }
        } catch (error) {
          console.error("Failed to disconnect Jira:", error);
          throw error;
        }
      },

      checkLinearStatus: async (workspaceId: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/linear/${workspaceId}/status`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          set((state) => ({
            integrations: {
              ...state.integrations,
              linear: {
                connected: data.connected || false,
                email: data.email,
                organizationName: data.organizationName,
              },
            },
          }));

          return data;
        } catch (error) {
          console.error("Failed to check Linear status:", error);
          set((state) => ({
            integrations: {
              ...state.integrations,
              linear: {
                connected: false,
              },
            },
          }));
        }
      },

      disconnectLinear: async (workspaceId: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/linear/${workspaceId}/disconnect`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            set((state) => ({
              integrations: {
                ...state.integrations,
                linear: {
                  connected: false,
                },
              },
              selectedIntegration:
                state.selectedIntegration === "linear"
                  ? null
                  : state.selectedIntegration,
            }));
          }
        } catch (error) {
          console.error("Failed to disconnect Linear:", error);
          throw error;
        }
      },

      checkSentryStatus: async (workspaceId: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/workspaces/${workspaceId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const workspace = await response.json();

          set((state) => ({
            integrations: {
              ...state.integrations,
              sentry: {
                ...state.integrations.sentry,
                connected: !!workspace.sentry?.organizationSlug,
                organizationName: workspace.sentry?.organizationSlug,
                projectSlug: workspace.sentry?.projectSlug || "",
              },
            },
          }));
        } catch (error) {
          console.error("Failed to check Sentry status:", error);
          set((state) => ({
            integrations: {
              ...state.integrations,
              sentry: {
                connected: false,
              },
            },
          }));
        }
      },

      disconnectSentry: async (workspaceId: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/sentry/${workspaceId}/disconnect`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            set((state) => ({
              integrations: {
                ...state.integrations,
                sentry: {
                  connected: false,
                },
              },
              selectedIntegration:
                state.selectedIntegration === "sentry"
                  ? null
                  : state.selectedIntegration,
            }));
          }
        } catch (error) {
          console.error("Failed to disconnect Sentry:", error);
          throw error;
        }
      },

      fetchSentryProjects: async (workspaceId: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/sentry/projects/${workspaceId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const projects = await response.json();

          set((state) => ({
            integrations: {
              ...state.integrations,
              sentry: {
                ...state.integrations.sentry,
                projects,
              },
            },
          }));
        } catch (error) {
          console.error("Failed to fetch Sentry projects:", error);
        }
      },

      setSentryProject: async (workspaceId: string, projectSlug: string) => {
        try {
          const token = getCookie("token");

          const response = await fetch(
            `${API_URL}/sentry/${workspaceId}/project`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ projectSlug }),
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          set((state) => ({
            integrations: {
              ...state.integrations,
              sentry: {
                ...state.integrations.sentry,
                projectSlug,
              },
            },
          }));
        } catch (error) {
          console.error("Failed to set Sentry project:", error);
          throw error;
        }
      },
    }),
    {
      name: "integrations-storage",
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persistedState: any) => {
        if (!persistedState.integrations.linear) {
          persistedState.integrations.linear = {
            connected: false,
          };
        }
        if (!persistedState.integrations.sentry) {
          persistedState.integrations.sentry = {
            connected: false,
          };
        }
        return persistedState;
      },
    }
  )
);
